import React, { useState, useEffect } from 'react';
import {
  Database, PanelLeftClose, PanelLeftOpen, Menu, Globe
} from 'lucide-react';
import { useSecurity } from '../security/SecurityContext';
import { useAuth } from '../auth/AuthContext';
import { ForceChangePasswordModal } from '../components/ForceChangePasswordModal';
import { LicensorConsole } from '../components/LicensorConsole';
import { RenewalManager } from '../components/RenewalManager';
import IndustryMasterManager from '../components/IndustryMasterManager';
import ActiveSessionsPage from '../components/ActiveSessionsPage';
import PoolConfigManager from '../components/PoolConfigManager';
import CountryFiscalConfigPanel from '../components/CountryFiscalConfigPanel';
import StatusBar from '../components/StatusBar';
import NavBtn from './NavBtn';
import UserMenu from './UserMenu';
import StatusDropdown from './StatusDropdown';
import { NAV_ITEMS } from './NAV_ITEMS';
import Dashboard from '../pages/Dashboard';
import LicenseOperatorDashboard from '../pages/LicenseOperatorDashboard';
import LicensesPage from '../pages/LicensesPage';
import NoAccessFallback from '../pages/NoAccessFallback';
import AuditVault from '../pages/AuditVault';
import AccessControl from '../pages/AccessControl';
import Reports from '../pages/Reports';
import DBControl from '../pages/DBControl';
import KeySettings from '../pages/KeySettings';
import HelpKBPage  from '../pages/HelpKBPage';
import UserAuditPage from '../pages/UserAuditPage';
import PropagationAuditPage from '../pages/PropagationAuditPage';
import ERPWorkspacePage from '../pages/ERPWorkspacePage';
import IntegrationDocsPage from '../pages/IntegrationDocsPage';
import DBManagementPage from '../pages/DBManagementPage';
import SchemaSyncPage from '../pages/SchemaSyncPage';
import TranslationsEditor from '../pages/TranslationsEditor';
import CredentialLookupPage from '../pages/CredentialLookupPage';
import GlobalizationPack from '../pages/GlobalizationPack';
import ClientRegistrationPage from '../pages/ClientRegistrationPage';
import ModulePrivilegesPage from '../pages/ModulePrivilegesPage';
import TierVariationsPage from '../pages/TierVariationsPage';
import SubUsersPage from '../pages/SubUsersPage';
import TemplateDBSetupPage from '../pages/TemplateDBSetupPage';
import IndustryMappingPanel from '../components/IndustryMappingPanel';
import ContextHelpButton from '../components/ContextHelpButton';
import LanguageSwitcher from '../i18n/LanguageSwitcher';
import { useT } from '../i18n/I18nContext.jsx';

/** Mini two-tab wrapper for Customers 360 section */
function Customers360Section() {
  const [sub, setSub] = useState('clients');
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0, height: '100%' }}>
      <div style={{ display: 'flex', gap: 2, padding: '8px 16px 0', borderBottom: '1px solid var(--border, #e2e8f0)', background: 'var(--surface-2, #f8fafc)' }}>
        {[
          { id: 'clients',        label: '360° Customer Records' },
          { id: 'country-config', label: 'Country Config', icon: Globe },
        ].map(t => (
          <button key={t.id} onClick={() => setSub(t.id)}
            data-testid={`c360-sub-${t.id}`}
            style={{
              padding: '6px 14px', fontSize: 11, fontWeight: 700, cursor: 'pointer',
              border: 'none', background: 'none',
              borderBottom: sub === t.id ? '2px solid #3b82f6' : '2px solid transparent',
              color: sub === t.id ? '#3b82f6' : '#64748b',
              display: 'flex', alignItems: 'center', gap: 5, transition: 'color 0.15s'
            }}>
            {t.icon && <t.icon size={11} />}{t.label}
          </button>
        ))}
      </div>
      <div style={{ flex: 1, overflow: 'auto', padding: sub === 'country-config' ? '0 16px' : 0 }}>
        {sub === 'clients'        && <LicensorConsole forceTab="clients" />}
        {sub === 'country-config' && <CountryFiscalConfigPanel />}
      </div>
    </div>
  );
}

export default function AppContent() {
  const [tab, setTab] = useState('dashboard');
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() =>
    typeof window !== 'undefined' && localStorage.getItem('erp_sidebar_collapsed') === 'true'
  );
  const [drawerOpen, setDrawerOpen] = useState(false); // mobile/tablet off-canvas
  const toggleSidebar = () => {
    setIsSidebarCollapsed(prev => {
      const next = !prev;
      try { localStorage.setItem('erp_sidebar_collapsed', String(next)); } catch (e) { console.warn('[AppContent] Could not persist sidebar state:', e.message); }
      return next;
    });
  };
  // Close drawer whenever the user picks a nav item or hits Escape
  const handleNavPick = (id) => { setTab(id); setDrawerOpen(false); };
  useEffect(() => {
    if (!drawerOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') setDrawerOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [drawerOpen]);
  const { fingerprint, guardStatus, mssqlConnected, chainHeight } = useSecurity();
  const { logout, user } = useAuth();

  // Cross-component navigation events (e.g., dashboard pill → Licensor Console)
  useEffect(() => {
    const handler = (e) => {
      const detail = e.detail || {};
      if (detail.tab) setTab(detail.tab);
      // Stash sub-tab hint so freshly-mounted children can pick it up.
      if (detail.subTab) {
        sessionStorage.setItem('erp-nav-subtab', detail.subTab);
        sessionStorage.setItem('erp-nav-subtab-ts', String(Date.now()));
      }
    };
    window.addEventListener('erp-navigate', handler);
    return () => window.removeEventListener('erp-navigate', handler);
  }, []);

  // Check if user is super admin (SysCLIQ — full access including Licensor group)
  const isSuperAdmin = user?.isSuperAdmin || user?.email === 'msivan@myaccounts.in';
  // Check if user is admin support (ssadmin — Administrator group only, not Licensor group)
  const isAdminSupport = !isSuperAdmin && (
    user?.role === 'SUPPORTADMIN' || user?.userType === 'SUPPORTADMIN' ||
    user?.role === 'ADMINSUPPORT' || user?.userType === 'ADMINSUPPORT' ||
    (user?.username && user.username.toLowerCase().startsWith('ssadmin'))
  );
  const t = useT();

  // Filter nav items based on user permissions
  const navItems = NAV_ITEMS.filter(item =>
    !item.superAdminOnly || isSuperAdmin || (item.adminSupportAllowed && isAdminSupport)
  );

  const tabLabel = {
    dashboard:   isSuperAdmin ? 'Security Command Center' : 'License Operator Dashboard',
    licenses:    'License Registry',
    licensor:    'Licensor Console — 360° Operations',
    'license-registry':   'License Registry — All Tiers',
    'customers-360':      '360° Customer Records',
    'renewal-manager':    'Renewal Manager',
    payments:             'Payments',
    'signup-metadata':    'Signup Metadata · Telemetry',
    'sub-users':          'Sub-Users (CLID) · Tenant User Sync',
    'pricing-tiers':      'Pricing & Tiers',
    'industry-matrix':    'Industry Module Matrix · Scoped module entitlements',
    'template-db-setup':  'Template DB Setup · Industry × Tier provisioning overrides',
    'module-privileges':  'Module Privileges · Tier × Screen Matrix',
    'tier-variations':    'Tier Variations · Compare Tier Entitlements',
    'erp-workspace':      'ERP Workspace Catalog · Dynamic Screen & Module Manager',
    'industry-master':    'Industry Master · CRUD',
    'active-sessions':    'Active Sessions · Live status across all tenants',
    translations:         'Translations Registry · Globalization Editor',
    'globalization-pack': 'Globalization Pack · i18n Integration Bundle',
    'schema-relations':   'Schema & Relations · DB Architecture Map',
    audit:       'Blockchain Audit Trail',
    'user-audit': 'User Audit Trail · Action Log',
    'propagation-audit': 'DB Propagation Centre · Sync Health & Audit Log',
    users:       'Identity Access Control',
    'pool-config': 'Pool Configuration · Signup overflow + TemplateDB',
    reports:     'System Reports',
    keysettings: 'Key Settings & DNA Config',
    masterdb:    'RC26masterDB',
    'help-kb':   'Help KB · Knowledge Base',
    'integration-docs': 'Integration Docs · Signup & ERP Integration Guide',
    'db-management':    'DB Management · Health Dashboard & Cleanup',
    'schema-sync':      'Schema Sync · Drift Report & Additive Auto-fix',
    'credential-lookup': 'Credential Lookup · Client ERP Accounts & Password Hashes',
    'client-registration': 'Client Registration · On-Premises, Hybrid & Cloud-Migration Clients',
  }[tab];

  return (
    <div className="app-layout">
      {/* Force password change overlay — blocks everything until resolved */}
      <ForceChangePasswordModal />

      {/* Mobile/tablet drawer backdrop */}
      <div
        className={`sidebar-backdrop ${drawerOpen ? 'drawer-open' : ''}`}
        onClick={() => setDrawerOpen(false)}
        data-testid="sidebar-backdrop"
      />

      {/* Sidebar */}
      <aside className={`sidebar ${isSidebarCollapsed ? 'collapsed' : ''} ${drawerOpen ? 'drawer-open' : ''}`}>
        <div className="sidebar-logo">
          <div className="brand-logo-wrap">
            <img
              src="/assets/retailcliq-logo-inverse.png"
              alt="RetailCLIQ"
              className="brand-logo brand-logo-dark"
              data-testid="brand-logo-sidebar"
            />
            <img
              src="/assets/retailcliq-logo-full.png"
              alt="RetailCLIQ"
              className="brand-logo brand-logo-light"
            />
          </div>
          <div className="sidebar-tagline" style={{ marginTop: 10, fontSize: 8, color: 'var(--sidebar-section-label)', letterSpacing: '0.15em', textTransform: 'uppercase', fontWeight: 700 }}>
            Enterprise DNA Security · vEritEqcLiQ
          </div>
        </div>

          <nav className="sidebar-nav">
          {(() => {
            let lastGroup = null;
            return navItems.map(item => {
              const showHeader = item.group && item.group !== lastGroup && !isSidebarCollapsed;
              lastGroup = item.group || lastGroup;
              return (
                <React.Fragment key={item.id}>
                  {showHeader && <div className="nav-section-label" style={{ marginTop: 12 }}>{item.group}</div>}
                  <NavBtn active={tab === item.id} onClick={() => handleNavPick(item.id)} Icon={item.icon} label={t(item.key, item.label)} collapsed={isSidebarCollapsed} />
                </React.Fragment>
              );
            });
          })()}

          <div className="nav-section-label" style={{ marginTop: 16 }}>Data Systems</div>
          <NavBtn active={tab === 'masterdb'} onClick={() => handleNavPick('masterdb')} Icon={Database} label="RC26masterDB" collapsed={isSidebarCollapsed} />
        </nav>

        <div className="sidebar-footer">
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
            <span className={`status-dot ${mssqlConnected ? 'dot-green' : 'dot-amber'} dot-pulse`} />
            <span className="sidebar-footer-text" style={{ fontSize: 10 }}>{mssqlConnected ? 'RC26masterDB Connected' : 'File-Only Mode'}</span>
          </div>
          <div className="sidebar-footer-text" style={{ color: 'var(--sidebar-section-label)', fontSize: 9 }}>⛓ Chain Height: {chainHeight}</div>
          <div className="sidebar-footer-text" style={{ color: 'var(--sidebar-section-label)', fontSize: 9, marginTop: 2 }}>Version 4.2.1-Enterprise</div>
        </div>
      </aside>

      {/* Main */}
      <div className="main-content">
        <header className="topbar">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {/* Mobile hamburger (visible < 1024px via .mobile-menu-trigger CSS) */}
            <button
              onClick={() => setDrawerOpen(true)}
              title="Open menu"
              data-testid="mobile-menu-trigger"
              className="mobile-menu-trigger"
              aria-label="Open navigation menu"
            >
              <Menu size={18} />
            </button>
            <button
              onClick={toggleSidebar}
              title={isSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              data-testid="sidebar-toggle"
              className="sidebar-toggle-btn desktop-only-sidebar-toggle"
              style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                width: 32, height: 32, borderRadius: 8,
                border: '1px solid var(--border, #e2e8f0)',
                background: 'var(--surface-3, #f1f5f9)',
                color: 'var(--text-primary, #0f172a)',
                cursor: 'pointer', transition: 'background 150ms'
              }}
              onMouseEnter={e => { e.currentTarget.style.background = 'var(--sidebar-hover, #e2e8f0)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'var(--surface-3, #f1f5f9)'; }}
            >
              {isSidebarCollapsed ? <PanelLeftOpen size={16} /> : <PanelLeftClose size={16} />}
            </button>
            <div className="breadcrumb">
              <span>Home</span>
              <span className="breadcrumb-sep">/</span>
              <span className="breadcrumb-current">{tabLabel}</span>
            </div>
          </div>
          <div className="topbar-right">
            <StatusDropdown
              guardStatus={guardStatus}
              isAdmin={user?.isAdmin || user?.isSuperAdmin}
              mssqlConnected={mssqlConnected}
            />
            <LanguageSwitcher compact />
            <UserMenu user={user} onLogout={logout} />
          </div>
        </header>

        <div className="page-area">
          {tab === 'dashboard'   && (isSuperAdmin ? <Dashboard /> : <LicenseOperatorDashboard />)}
          {tab === 'licenses'    && <LicensesPage />}
          {tab === 'licensor'    && (isSuperAdmin ? <LicensorConsole /> : <NoAccessFallback feature="Licensor Console" />)}
          {tab === 'license-registry'   && (isSuperAdmin ? <LicensorConsole forceTab="licenses"  /> : <NoAccessFallback feature="License Registry" />)}
          {tab === 'customers-360'      && (isSuperAdmin ? <Customers360Section /> : <NoAccessFallback feature="Customers 360" />)}
          {tab === 'renewal-manager'    && (isSuperAdmin ? <RenewalManager />                    : <NoAccessFallback feature="Renewal Manager" />)}
          {tab === 'payments'           && (isSuperAdmin ? <LicensorConsole forceTab="payments"  /> : <NoAccessFallback feature="Payments" />)}
          {tab === 'signup-metadata'    && (isSuperAdmin ? <LicensorConsole forceTab="metadata"  /> : <NoAccessFallback feature="Signup Metadata" />)}
          {tab === 'sub-users'          && (isSuperAdmin ? <SubUsersPage />                          : <NoAccessFallback feature="Sub-Users (CLID)" />)}
          {tab === 'pricing-tiers'      && (isSuperAdmin ? <LicensorConsole forceTab="pricing" pricingSubTab="pricing" /> : <NoAccessFallback feature="Pricing & Tiers" />)}
          {tab === 'industry-matrix'    && (isSuperAdmin ? <IndustryMappingPanel /> : <NoAccessFallback feature="Industry × Module Matrix" />)}
          {tab === 'template-db-setup'  && (isSuperAdmin ? <TemplateDBSetupPage /> : <NoAccessFallback feature="Template DB Setup" />)}
          {tab === 'module-privileges'  && (isSuperAdmin ? <ModulePrivilegesPage /> : <NoAccessFallback feature="Module Privileges" />)}
          {tab === 'tier-variations'    && (isSuperAdmin ? <TierVariationsPage /> : <NoAccessFallback feature="Tier Variations" />)}
          {tab === 'erp-workspace'      && (isSuperAdmin ? <ERPWorkspacePage /> : <NoAccessFallback feature="ERP Workspace Catalog" />)}
          {tab === 'industry-master'    && (isSuperAdmin ? <IndustryMasterManager /> : <NoAccessFallback feature="Industry Master" />)}
          {tab === 'active-sessions'    && (isSuperAdmin ? <ActiveSessionsPage /> : <NoAccessFallback feature="Active Sessions" />)}
          {tab === 'translations'       && (isSuperAdmin ? <TranslationsEditor /> : <NoAccessFallback feature="Translations" />)}
          {tab === 'globalization-pack' && (isSuperAdmin ? <GlobalizationPack /> : <NoAccessFallback feature="Globalization Pack" />)}
          {tab === 'schema-relations'   && (isSuperAdmin ? <LicensorConsole forceTab="relations" /> : <NoAccessFallback feature="Schema & Relations" />)}
          {tab === 'audit'       && <AuditVault />}
          {tab === 'users'       && <AccessControl />}
          {tab === 'pool-config' && (isSuperAdmin || isAdminSupport ? <PoolConfigManager /> : <NoAccessFallback feature="Pool Configuration" />)}
          {tab === 'reports'     && <Reports />}
          {tab === 'keysettings' && (isSuperAdmin || isAdminSupport ? <KeySettings /> : <NoAccessFallback feature="Key Settings" />)}
          {tab === 'help-kb'          && (isSuperAdmin || isAdminSupport ? <HelpKBPage  /> : <NoAccessFallback feature="Help KB" />)}
          {tab === 'integration-docs' && (isSuperAdmin || isAdminSupport ? <IntegrationDocsPage /> : <NoAccessFallback feature="Integration Docs" />)}
          {tab === 'db-management'    && (isSuperAdmin || isAdminSupport ? <DBManagementPage /> : <NoAccessFallback feature="DB Management" />)}
          {tab === 'schema-sync'      && (isSuperAdmin || isAdminSupport ? <SchemaSyncPage /> : <NoAccessFallback feature="Schema Sync" />)}
          {tab === 'credential-lookup' && (isSuperAdmin ? <CredentialLookupPage /> : <NoAccessFallback feature="Credential Lookup" />)}
          {tab === 'user-audit'  && (isSuperAdmin || isAdminSupport ? <UserAuditPage /> : <NoAccessFallback feature="User Audit Trail" />)}
          {tab === 'propagation-audit' && (isSuperAdmin || isAdminSupport ? <PropagationAuditPage /> : <NoAccessFallback feature="DB Propagation Centre" />)}
          {tab === 'client-registration' && (isSuperAdmin || isAdminSupport ? <ClientRegistrationPage isSuperAdmin={isSuperAdmin} isLicensor={user?.username === 'HVSNKCVNM'} /> : <NoAccessFallback feature="Client Registration" />)}
          {tab === 'masterdb'    && <DBControl />}
        </div>
      </div>

      {/* Contextual "?" help button — mapped to current screen's KB article */}
      {tab && <ContextHelpButton screenId={tab} screenLabel={tabLabel || tab} />}

      {/* Bottom-sticky Windows-style status bar */}
      <StatusBar user={user} />
    </div>
  );
}
